import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";

import { formatCurrency } from "helpers/currency";
import { MAX_DEATH_CERTIFICATES } from "constants/at-need";

import type { ServiceQuestion, ServiceAnswer } from "./types";
import { deathCertCost } from "./helpers";

/**
 * The quantity half of the death certificate question: included count, per-copy
 * pricing, the stepper and the subtotal.
 *
 * Extracted verbatim from StepperQuestion so the legacy screen and the
 * self-serve screen render exactly the same thing. Both use it, so neither can
 * drift from the other — keep it that way rather than restating this markup in
 * either caller.
 */
export default function DeathCertStepper({
  question,
  answer,
  onAnswer,
  readOnly = false,
  orderedQuantity,
}: {
  question: ServiceQuestion;
  answer: ServiceAnswer | undefined;
  onAnswer: (answer: ServiceAnswer) => void;
  /** Blocks edits, for as long as the count isn't the family's to change. */
  readOnly?: boolean;
  /**
   * How many copies the case records as ordered, once an order exists. Shown
   * as it stands: it is what was ordered and paid for, so the plan's included
   * count is not a fallback for it, and a case that recorded no number ordered
   * none. Absent until an order is placed, which is also the only state the
   * "have been ordered" wording is true of.
   */
  orderedQuantity?: number;
}) {
  const config = question.stepperConfig!;
  const includedQty = config.includedQuantity ?? 0;
  const isOrdered = orderedQuantity !== undefined;
  const quantity = isOrdered
    ? orderedQuantity
    : (answer?.quantity ?? includedQty);

  const total = deathCertCost(
    quantity,
    includedQty,
    config.firstCopyPrice,
    config.additionalCopyPrice,
  );

  const handleChange = (newQty: number) => {
    const clamped = Math.max(
      includedQty,
      Math.min(MAX_DEATH_CERTIFICATES, newQty),
    );
    onAnswer({ questionId: question.id, quantity: clamped });
  };

  return (
    <>
      {includedQty > 0 && (
        <p className="mb-4 text-lg font-bold text-soil">
          {includedQty} included in your plan
        </p>
      )}

      <ul className="mb-6 list-disc space-y-1 pl-5 text-base text-soil">
        <li>
          First {includedQty > 0 ? "additional " : ""}copy:{" "}
          <span className="font-medium text-evergreen">
            {formatCurrency(config.firstCopyPrice)}
          </span>
        </li>
        <li>
          Each copy after:{" "}
          <span className="font-medium text-evergreen">
            {formatCurrency(config.additionalCopyPrice)}
          </span>
        </li>
      </ul>

      <p className="mb-4 text-sm text-soil">
        {isOrdered
          ? `${quantity} ${quantity === 1 ? "copy has" : "copies have"} been ordered.`
          : `You can order up to ${MAX_DEATH_CERTIFICATES} certificates.`}
      </p>

      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={() => handleChange(quantity - 1)}
          disabled={readOnly || quantity <= includedQty}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-olive text-evergreen transition hover:bg-oat disabled:opacity-40"
          aria-label="Decrease quantity"
        >
          <MinusIcon className="h-4 w-4" />
        </button>
        <span className="w-8 text-center text-xl font-bold text-evergreen">
          {quantity}
        </span>
        <button
          type="button"
          onClick={() => handleChange(quantity + 1)}
          disabled={readOnly || quantity >= MAX_DEATH_CERTIFICATES}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-olive text-evergreen transition hover:bg-oat disabled:opacity-40"
          aria-label="Increase quantity"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      </div>

      {total > 0 && (
        <p className="mt-4 text-xl font-bold text-evergreen">
          Subtotal: {formatCurrency(total)}
        </p>
      )}
    </>
  );
}
